// frontend/src/contexts/AnalyticsContext.jsx
import { createContext, useContext, useCallback, useEffect } from 'react';
import { analyticsService } from '../services/analytics';
import {
  getInteractiveAncestor,
  isTrackingIgnored,
  isTrackingHandled,
  buildClickContext,
} from '../utils/analyticsElementContext';
import logger from '../utils/logger';

const AnalyticsContext = createContext(null);

export const AnalyticsProvider = ({ children }) => {
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (analyticsService.hasConsent) {
        analyticsService.flushPending();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Universal fallback: catches every interactive click app-wide so no
  // button goes untracked, even ones without a bespoke track() call.
  // Skipped when the click is opted out (data-analytics-ignore) or already
  // covered by a component's own named event (data-track-handled).
  useEffect(() => {
    const handleDocumentClick = (event) => {
      const target = getInteractiveAncestor(event.target);
      if (!target) return;
      if (isTrackingIgnored(target) || isTrackingHandled(target)) return;

      analyticsService.track('button_click', buildClickContext(target));
    };

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, []);

  const track = useCallback((actionType, metadata = {}) => {
    analyticsService.track(actionType, metadata);
  }, []);

  // Consolidated helper for explicit, semantically-named events: auto-merges
  // page/button/game context from the triggering DOM element (when passed)
  // so call sites don't have to manually thread game_id/game_name that
  // already available via data-game-* ancestors. Explicit metadata wins.
  const trackClick = useCallback((actionType, metadata = {}, sourceElement = null) => {
    analyticsService.track(actionType, buildClickContext(sourceElement, metadata));
  }, []);

  const trackPageView = useCallback((pageName, metadata = {}) => {
    analyticsService.track('page_view', { page_name: pageName, ...metadata });
  }, []);

  const trackFormSubmit = useCallback((formName, metadata = {}) => {
    analyticsService.track('form_submit', { form: formName, ...metadata });
  }, []);

  const value = {
    track,
    trackPageView,
    trackClick,
    trackFormSubmit,
    hasConsent: () => true,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) {
    logger.warn('useAnalytics called outside AnalyticsProvider');
    // Return no-op functions so components don't crash
    return {
      track: () => {},
      trackPageView: () => {},
      trackClick: () => {},
      trackFormSubmit: () => {},
      hasConsent: () => true,
    };
  }
  return ctx;
};

export default AnalyticsContext;
